/**
 * ApprovalHistoryDialog (Story 24.6 — Epic 24: Report Approval Workflow)
 *
 * Read-only §4.4 modal showing a report's append-only approval timeline
 * (submissions, reviews, who, when, comments). Dialog-scoped lazy fetch
 * (`enabled: open`) of GET /api/reports/:id/approval-history. No mutations.
 */

import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { apiGet } from "../../lib/apiClient";
import type { ReportListItem } from "./ReportTableView";

export type ApprovalEventAction =
  | "submitted"
  | "assigned"
  | "approved"
  | "changes_requested"
  | "withdrawn"
  | "resubmitted";

interface ApprovalEvent {
  id: number;
  action: ApprovalEventAction;
  actorName: string;
  comment: string | null;
  changedAt: number;
}

interface ApprovalHistoryResult {
  events: ApprovalEvent[];
  capped: boolean;
}

// SYNC: keep in step with APPROVAL_ACTION_VALUES in packages/db (report-approvals.ts).
const ACTION_LABEL: Record<ApprovalEventAction, string> = {
  submitted: "Submitted for review",
  assigned: "Reviewer assigned",
  approved: "Approved",
  changes_requested: "Changes requested",
  withdrawn: "Withdrawn",
  resubmitted: "Resubmitted for review",
};

function formatTs(ts: number): string {
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ApprovalHistoryDialogProps {
  report: ReportListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ApprovalHistoryDialog({
  report,
  open,
  onOpenChange,
}: ApprovalHistoryDialogProps) {
  const reportId = report?.id ?? null;
  const { data, isLoading, isError } = useQuery<ApprovalHistoryResult>({
    queryKey: ["report-approval-events", reportId],
    enabled: open && !!reportId,
    queryFn: async () => {
      const res = await apiGet(`/api/reports/${reportId}/approval-history`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Failed to load history" }));
        throw new Error(err.detail ?? "Failed to load history");
      }
      const json = await res.json();
      // Defensive: a malformed 200 falls back to an empty timeline, not a throw.
      return {
        events: (json?.data?.events ?? []) as ApprovalEvent[],
        capped: Boolean(json?.data?.capped),
      };
    },
  });
  const events = data?.events ?? [];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none max-h-[85vh] overflow-y-auto"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="approval-history-dialog"
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Approval history
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {report ? `Timeline for "${report.name}".` : "Approval timeline."}
                  </DialogPrimitive.Description>
                </div>

                <div className="mt-6">
                  {isLoading ? (
                    <p className="text-[13px] text-muted-foreground">Loading history…</p>
                  ) : isError ? (
                    <p className="text-[13px] text-destructive" role="alert">
                      Couldn't load the approval history.
                    </p>
                  ) : events.length > 0 ? (
                    <>
                      <ol className="space-y-4">
                        {events.map((e) => (
                          <li key={e.id} className="flex flex-col gap-0.5">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-[13px] font-medium text-foreground">
                                {ACTION_LABEL[e.action] ?? e.action}
                              </span>
                              <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                {formatTs(e.changedAt)}
                              </span>
                            </div>
                            <span className="text-[12px] text-muted-foreground">
                              by {e.actorName}
                            </span>
                            {e.comment && (
                              <p className="mt-1 text-[12px] text-foreground bg-muted/50 rounded-lg px-2 py-1.5">
                                {e.comment}
                              </p>
                            )}
                          </li>
                        ))}
                      </ol>
                      {data?.capped && (
                        <p
                          className="mt-3 text-[11px] text-muted-foreground"
                          data-testid="approval-history-capped"
                        >
                          Showing the first 100 events.
                        </p>
                      )}
                    </>
                  ) : (
                    // Only show "empty" while genuinely open — avoids the timeline
                    // blinking to empty during the close/exit animation (report→null).
                    open && (
                      <p
                        className="text-[13px] text-muted-foreground"
                        data-testid="approval-history-empty"
                      >
                        No approval activity yet.
                      </p>
                    )
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => onOpenChange(false)}
                  >
                    Close
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
